// components/CreatePool.js
import React, { useState, useEffect } from "react";
import "./createpool.scss";
import { useAccount } from "wagmi";
import Loader from '../../shared/Loader';
import { useTokenBalance, usePoolCreation, useFormValidation } from './hooks';
import { StepIndicator } from './components';
import { BasicInfoStep, RewardStep, FinalizeStep } from './steps';
import { SuccessModal, FailureModal } from './modals';
import { useTokenAllowance } from "./hooks/useTokenAllowance";
import useIPFSMetadata from "../../hooks/useIPFSMetadata";
import usePoolAPI from "./hooks/usePoolAPI";
import { useTheme } from "../../hooks/ThemeProvider";
import { useChainSwitch } from "../../hooks/useChainSwitch";
import { useHistory } from "react-router-dom";

const CreatePool = () => {
  const { theme } = useTheme();
  const { address: account } = useAccount();
  const [loadingText, setLoadingText] = useState("");
  const { switchToChain } = useChainSwitch();
  const history = useHistory()
  // Form data state
  const [formData, setFormData] = useState({
    logo: null,
    logoPreview: null,
    rewardToken: "",
    chain: "",
    projectName: "",
    tagline: "",
    poolDescription: "",
    website: "",
    twitter: "",
    instagram: "",
    discord: "",
    totalRewards: "",
    poolEndDate: null,
    symbol: "",
    tokenDecimals: "",
  });
// console.log(formData, 'formDataformDataformData')
  // Error states
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [apiRes, setApiRes] = useState(null);
  const [loader, setLoader] = useState(false);
  const [trxHash, setTrxHash] = useState("");

  // Custom hooks
  const { tokenBalance, tokenDecimals, isCheckingBalance, tokenSymbol, checkTokenBalance } = useTokenBalance();
  const {
    currentAllowance,
    isCheckingAllowance,
    isApprovingToken,
    ensureAllowance,
    checkAllowance,
    approveToken,
    hasInsufficientAllowance
  } = useTokenAllowance();
  const { generateIPFSMetadata, isUploadingMetadata, metadataError } = useIPFSMetadata();
  const { isCreatingPool, createPool } = usePoolCreation();
  const { validateStep1, validateStep2 } = useFormValidation(tokenBalance);
  const { savePoolToBackend, isSavingPool, saveError } = usePoolAPI();

  // Handle input changes
  const handleInputChange = (field, value, additionalErrors = {}) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing and set additional errors if any
    setErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[field]) {
        delete newErrors[field];
      }
      return { ...newErrors, ...additionalErrors };
    });
  };

  // Check token balance when reward token address changes
  useEffect(() => {
    if (formData.rewardToken && account) {
      // Basic address validation
      if (!/^0x[a-fA-F0-9]{40}$/.test(formData.rewardToken)) {
        setErrors(prev => ({ ...prev, rewardToken: "Invalid EVM address format" }));
        return;
      }

      checkTokenBalance(formData.rewardToken, account);
    }
  }, [formData.rewardToken, account, checkTokenBalance]);

  const handleNextStep = (step) => {
    let validationErrors = {};

    // console.log('handleNextStep called for step:', step);
    // console.log('Current form data:', formData);

    if (step === 2) {
      // console.log('Validating step 1...');
      validationErrors = validateStep1(formData);

      if (isCheckingBalance) {
        // console.log('Still checking balance, adding balance error');
        validationErrors.rewardToken = "Please wait while we check your token balance";
      }
    }

    if (step === 3) {
      // console.log('Validating step 2...');
      validationErrors = validateStep2(formData);
    }

    // console.log('Validation errors found:', validationErrors);
    // console.log('Number of validation errors:', Object.keys(validationErrors).length);

    if (Object.keys(validationErrors).length === 0) {
      // console.log('No validation errors, proceeding to step:', step);
      setCurrentStep(step);
    } else {
      // console.log('Validation failed, setting errors and staying on current step');
      setErrors(validationErrors);

      // Scroll to first error (optional)
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`input[value="${formData[firstErrorField] || ''}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Handle date change
  const handleDateChange = (date) => {
    handleInputChange('poolEndDate', date);
  };

  // Handle pool creation
  const handleCreatePool = async () => {
    // // console.log(formData, 'formDataformDataformData')
    // return
    const validationErrors = validateStep2(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await switchToChain();
      setLoader(true);
      setLoadingText("Generating IPFS metadata...");
      // console.log('Generating IPFS metadata...');
      const metadataResult = await generateIPFSMetadata(formData);

      if (!metadataResult.success) {
        throw new Error(`Failed to upload metadata: ${metadataResult.error}`);
      }

      // console.log('Metadata uploaded to IPFS:', metadataResult.ipfsLink);

      setLoadingText("Checking token allowance...");
      await ensureAllowance(
        formData.rewardToken,
        account,
        formData.totalRewards
      );

      setLoadingText("Creating pool on blockchain...");
      const receipt = await createPool(
        account,
        formData.rewardToken,
        formData.totalRewards,
        formData.poolEndDate,
        metadataResult.ipfsLink || '',
        formData.tokenDecimals
      );

      setTrxHash(receipt.hash);
      // console.log("Pool created successfully:", receipt);

      // Call backend API after successful pool creation
      setLoadingText("Saving pool data...");
      const apiResult = await savePoolToBackend(formData, account, receipt.hash, receipt?.unifarmAddress);

      if (!apiResult.success) {
        console.warn("Pool created on blockchain but failed to save to backend:", apiResult.error);
        // You can decide whether to show success or failure modal here
        // Currently showing success since blockchain transaction succeeded
      }
      setApiRes(apiResult?.data?.data);
      setLoadingText("Finalizing...");
      setShowSuccessModal(true);
      setLoader(false);

    } catch (error) {
      console.error("Failed to create pool:", error);
      setShowFailureModal(true);
      setLoader(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            formData={formData}
            errors={errors}
            isCheckingBalance={isCheckingBalance}
            onInputChange={handleInputChange}
          />
        );
      case 2:
        return (
          <RewardStep
            formData={formData}
            errors={errors}
            tokenBalance={tokenBalance}
            tokenSymbol={tokenSymbol}
            tokenDecimals={tokenDecimals}
            onInputChange={handleInputChange}
            onDateChange={handleDateChange}
          />
        );
      case 3:
        return (
          <FinalizeStep
            formData={formData}
            tokenSymbol={tokenSymbol}
            currentStep={currentStep}
          />
        );
      default:
        return null;
    }
  };

  // Render navigation buttons
  const renderNavigationButtons = () => {
    if (currentStep === 1) {
      return (
        <div className="bottom-btns">
          <button
            onClick={() => handleNextStep(2)}
            className="continuebtnmain"
          >
            Continue
          </button>
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="bottom-btns">
          <button
            className="backbtnbtn"
            onClick={handlePreviousStep}
          >
            Back
          </button>
          <button
            onClick={() => handleNextStep(3)}
            className="continuebtn"
          >
            Continue
          </button>
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div className="bottom-btns">
          <button
            className="backbtnbtn"
            onClick={handlePreviousStep}
          >
            Back
          </button>
          <button
            className="continuebtn"
            onClick={handleCreatePool}
            disabled={isCreatingPool}
          >
            {isCreatingPool ? 'Creating Pool...' : 'Stake & Finalize'}
          </button>
        </div>
      );
    }

    return null;
  };
  useEffect(() => {
    if (tokenSymbol && tokenDecimals) {
      setFormData(prev => ({ ...prev, symbol: tokenSymbol, tokenDecimals  }) );
    }
  }, [tokenSymbol, tokenDecimals]);
  return (
    <>
      {loader && <Loader loadingText={loadingText} />}

      <section className="createpool">
 {
        theme === 'dark' ? <img src="\assets\bannershade.png" className="bannershade" alt="banner" />
          : <img src='\assets\whiteshade.png' className='bannershade' />
      }

        <div className="custom-container">
          <div className="mainbox">
            <div className="upperhead">
              <h2>Create Root Pool</h2>
              <p>Lock rewards, set duration, and launch your farm.</p>
            </div>

            <div className="stepscontent">
              <div className="innerpublicpoll">
                <StepIndicator currentStep={currentStep} />

                {renderStepContent()}

                {renderNavigationButtons()}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SuccessModal
        show={showSuccessModal}
        onClose={() => { setShowSuccessModal(false); history.push("/") }}
        formData={formData}
        trxHash={trxHash}
        apiRes={apiRes}
        tokenSymbol={tokenSymbol}
      />

      <FailureModal
        show={showFailureModal}
        onClose={() => setShowFailureModal(false)}
      />
    </>
  );
};

export default CreatePool;